import React, { useState } from 'react';
import { Card, Button, Spinner } from '@/components/ui';

interface ReportParametersCardProps {
  children: React.ReactNode;
  onGenerate: () => void;
  onExport: () => void;
  loadingGenerate: boolean;
  disableExport: boolean;
  loadingExport?: boolean;
  title?: string;
  rightColumn?: React.ReactNode;
  generateButtonText?: string;
  hideExportButton?: boolean;
  defaultCollapsed?: boolean;
  onCollapseChange?: (isCollapsed: boolean) => void; // New prop to expose collapse state
  /** Disable Run (e.g. required filters missing); the reason shows as the button's tooltip. */
  disableGenerate?: boolean;
  generateDisabledReason?: string;
}

const ReportParametersCard: React.FC<ReportParametersCardProps> = ({
  children,
  onGenerate,
  onExport,
  loadingGenerate,
  disableExport,
  loadingExport,
  title = 'Report Parameters',
  rightColumn,
  generateButtonText = 'Generate Preview',
  hideExportButton = false,
  defaultCollapsed = false,
  onCollapseChange,
  disableGenerate = false,
  generateDisabledReason
}) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapseChange?.(newCollapsedState); // Notify parent of collapse state change
  };

  return (
    <Card className="tw-mb-4">
      <Card.Header className="tw-flex tw-justify-between tw-items-center">
        <h5 className="tw-mb-0">{title}</h5>
        {/* The standard filter collapse control (same as FiltersCard): an icon-only chevron. */}
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={toggleCollapse}
          aria-expanded={!isCollapsed}
          aria-label={isCollapsed ? 'Expand filters' : 'Collapse filters'}
          title={isCollapsed ? 'Expand filters' : 'Collapse filters'}
        >
          <i className={`bi ${isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up'}`} aria-hidden="true"></i>
        </Button>
      </Card.Header>
      {!isCollapsed && (
        <Card.Body>
          {/* Stack on mobile/tablet instead of a fixed 9/3 split: Col md={9}/md={3} jumps straight
              from full-width to a fixed fraction at the md breakpoint, so on a phone or tablet the
              action buttons squeeze into a narrow strip next to the filters. Row/column below md,
              side-by-side at md+, and the action column never shrinks past its own content. */}
          <div className="tw-flex tw-flex-col md:tw-flex-row md:tw-items-end tw-gap-3">
            <div className="tw-min-w-0 tw-flex-1">{children}</div>
            <div className="tw-flex tw-flex-row tw-flex-wrap tw-gap-2 md:tw-flex-shrink-0 md:tw-flex-col md:tw-items-end">
              {rightColumn ? (
                rightColumn
              ) : (
                <>
                  <Button
                    variant="primary"
                    onClick={onGenerate}
                    disabled={loadingGenerate || disableGenerate}
                    title={disableGenerate ? generateDisabledReason : undefined}
                  >
                    {loadingGenerate ? <><Spinner animation="border" size="sm" className="tw-mr-2" />Generating...</> : generateButtonText}
                  </Button>
                  {!hideExportButton && (
                    <Button
                      variant="success"
                      onClick={onExport}
                      disabled={disableExport || loadingExport}
                    >
                      {loadingExport ? <><Spinner animation="border" size="sm" className="tw-mr-2" />Exporting...</> : 'Export Report'}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </Card.Body>
      )}
    </Card>
  );
};

export default ReportParametersCard; 